import type { FlightTrack, TrackPoint } from "./aar";
import { opsKinematicsFromFused, type OpsStripRow } from "./opsStrip";
import { positionLabel, type FusedPosition, type PositionSource } from "./position";
import { formatMapCallsign } from "./radio";

/**
 * One company aircraft on the ops map. Solo desks emit length 0 or 1.
 * Multiplayer later maps N live company aircraft into the same array —
 * hooks only: no public ADS-B and no position sync in this slice.
 * VATSIM map traffic stays a separate overlay (`vatsimOverlay.ts`).
 * VATSIM ops assist may feed the fused sample when SI is not in play —
 * this array still only paints usable company fixes.
 * Desk chrome (strip / status / map labels) — not a consumer tracking skin.
 */
export type CompanyRadarTarget = {
  id: string;
  /** Callsign, else tail, else type, else `ACFT`. */
  label: string;
  /** Tail when the strip has one — map hover prefers this over a trip callsign. */
  registration: string;
  lat: number;
  lon: number;
  headingDeg: number | null;
  headingKind: "track" | "heading" | null;
  altitudeFt: number | null;
  groundspeedKt: number | null;
  altitudeLabel: string;
  groundspeedLabel: string;
  headingLabel: string;
  squawkLabel: string;
  eteLabel: string;
  phaseLabel: string;
  source: PositionSource;
  stale: boolean;
  trackId: string;
  track: TrackPoint[];
  /** Hover / tooltip: identity + dispatcher kinematics. */
  tooltip: string;
};

export type BuildCompanyRadarInput = {
  fused: FusedPosition;
  row?: OpsStripRow | null;
  track?: FlightTrack | null;
  callsign?: string | null;
};

function trim(value?: string | null): string {
  return typeof value === "string" ? value.trim() : "";
}

export function radarTargetLabel(input: {
  callsign?: string | null;
  row?: Pick<OpsStripRow, "callsign" | "registration" | "aircraftType"> | null;
}): string {
  return (
    trim(input.callsign) ||
    trim(input.row?.callsign) ||
    trim(input.row?.registration) ||
    trim(input.row?.aircraftType) ||
    "ACFT"
  );
}

/** Compact own-ship hover: hyphenated tail, then COM / FIR only when supplied. */
export function ownshipHoverText(input: {
  callsign?: string | null;
  registration?: string | null;
  comFreq?: string | null;
  fir?: string | null;
}): string {
  const id = formatMapCallsign(trim(input.registration) || trim(input.callsign));
  const fir = trim(input.fir).replace(/^FIR\s+/i, "");
  const firBit = fir && fir !== "—" ? `FIR ${fir.split(/\s+/)[0]}` : "";
  return [id, trim(input.comFreq), firBit].filter(Boolean).join("  ");
}

export function companyRadarTooltip(target: Pick<
  CompanyRadarTarget,
  | "label"
  | "altitudeLabel"
  | "groundspeedLabel"
  | "headingLabel"
  | "headingKind"
  | "squawkLabel"
  | "eteLabel"
  | "phaseLabel"
  | "source"
  | "stale"
  | "lat"
  | "lon"
>): string {
  const pos = positionLabel({
    lat: target.lat,
    lon: target.lon,
    heading: null,
    altitudeFt: null,
    altitudeAgl: null,
    airspeed: null,
    groundSpeed: null,
    verticalSpeedFpm: null,
    onGround: null,
    atUtc: "",
    source: target.source,
    stale: target.stale,
  });
  const hdg = target.headingKind === "track" ? "TRK" : "HDG";
  return [
    target.label,
    target.phaseLabel ? `PHASE ${target.phaseLabel}` : "",
    `ALT ${target.altitudeLabel === "—" ? "—" : `${target.altitudeLabel} ft`}`,
    `GS ${target.groundspeedLabel === "—" ? "—" : `${target.groundspeedLabel} kt`}`,
    `${hdg} ${target.headingLabel}`,
    `SQK ${target.squawkLabel}`,
    `ETE ${target.eteLabel}`,
    pos,
  ]
    .filter(Boolean)
    .join("  ");
}

export function companyRadarKinematicsLine(target: Pick<CompanyRadarTarget, "altitudeLabel" | "groundspeedLabel">): string {
  const bits = [
    target.altitudeLabel !== "—" ? `${target.altitudeLabel} ft` : "",
    target.groundspeedLabel !== "—" ? `${target.groundspeedLabel} kt` : "",
  ].filter(Boolean);
  return bits.join(" · ") || "—";
}

/** Trip-matching track first, else the newest list head. Solo has one live track. */
export function pickLiveTrack(
  tracks?: FlightTrack[] | null,
  mission?: { id: string } | null,
): FlightTrack | null {
  if (!tracks?.length) return null;
  if (mission?.id) {
    const match = tracks.find((t) => t.tripId === mission.id);
    if (match) return match;
  }
  return tracks[0] ?? null;
}

export function buildCompanyRadarTarget(input: BuildCompanyRadarInput): CompanyRadarTarget {
  const fused = input.fused;
  const kinematics = opsKinematicsFromFused(fused);
  const label = radarTargetLabel({ callsign: input.callsign, row: input.row });
  const track = input.track?.points?.length ? input.track.points : [];
  const target: CompanyRadarTarget = {
    id: trim(input.row?.id) || "solo",
    label,
    registration: trim(input.row?.registration),
    lat: fused.lat,
    lon: fused.lon,
    headingDeg: kinematics.headingDeg,
    headingKind: kinematics.headingKind,
    altitudeFt: kinematics.altitudeFt,
    groundspeedKt: kinematics.groundspeedKt,
    headingLabel: kinematics.headingLabel,
    altitudeLabel: kinematics.altitudeLabel,
    groundspeedLabel: kinematics.groundspeedLabel,
    squawkLabel: input.row?.squawkLabel ?? kinematics.squawkLabel,
    eteLabel: input.row?.eteLabel ?? "—",
    phaseLabel: input.row?.phase?.label ?? "",
    source: fused.source,
    stale: Boolean(fused.stale),
    trackId: input.track?.id ?? "",
    track,
    tooltip: "",
  };
  target.tooltip = companyRadarTooltip(target);
  return target;
}

/**
 * Solo: wrap the one fused fix (`inputs.length` 0 or 1). Later: map N
 * company aircraft into the same helper. No sync / ADS-B here.
 */
export function buildCompanyRadarTargets(inputs: BuildCompanyRadarInput[] | null | undefined): CompanyRadarTarget[] {
  const out: CompanyRadarTarget[] = [];
  for (const input of inputs ?? []) {
    if (!input?.fused || !Number.isFinite(input.fused.lat) || !Number.isFinite(input.fused.lon)) continue;
    if (Math.abs(input.fused.lat) > 90 || Math.abs(input.fused.lon) > 180) continue;
    out.push(buildCompanyRadarTarget(input));
  }
  return out;
}
